import type { ReactNode } from 'react';
import { CorePlatformSettings } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { SettingsEditor } from '../../components/SettingsEditor';
import { Card, PageHeader } from '../../components/ui';
import { Tabs, useTab } from '../../modules/sites/Tabs';
import { WalletPlatformCard } from '../../modules/tenant-setup/Wallet';
import { EmailServerSection } from './EmailServerSection';
import { PlatformEntraSecret, PlatformExpoSecret } from './PlatformSecretsSection';
import { PlatformSmsSection } from './PlatformSmsSection';

/**
 * Which top-level `core` platform setting groups each tab shows. Every group of CorePlatformSettings must be listed in
 * exactly one tab (a test checks this). The Advanced tab also shows any group not listed here, so a new setting never
 * goes missing.
 */
export const PLATFORM_TAB_GROUPS = {
  general: ['links', 'scheduling'],
  security: ['auth', 'support', 'entra'],
  email: ['email'],
  sms: [],
  wallet: [],
  /** Not a group of the core settings: the shared-reception module's own platform settings (its master switch and defaults). */
  sharedReception: [],
  apps: ['apps'],
  data: ['domains', 'tenantClosure', 'files'],
  advanced: ['realtime', 'jobs', 'api'],
} as const satisfies Record<string, readonly string[]>;

export type PlatformTabKey = keyof typeof PLATFORM_TAB_GROUPS;

export const PLATFORM_TABS: readonly { key: PlatformTabKey; label: string }[] = (
  ['general', 'security', 'email', 'sms', 'wallet', 'sharedReception', 'apps', 'data', 'advanced'] as const
).map((key) => ({ key, label: t(`platformTabs.${key}`) }));

const ASSIGNED = Object.entries(PLATFORM_TAB_GROUPS)
  .filter(([key]) => key !== 'advanced')
  .flatMap(([, groups]) => groups as readonly string[]);

/** Keys of CorePlatformSettings, for tests. */
export const CORE_PLATFORM_GROUPS: readonly string[] = Object.keys(CorePlatformSettings.shape);

function Groups({ tab, canEdit }: { tab: PlatformTabKey; canEdit: boolean }) {
  // Advanced = its own groups plus anything not placed on another tab.
  return tab === 'advanced' ? (
    <SettingsEditor moduleKey="core" base="/platform/settings" canEdit={canEdit} exceptGroups={ASSIGNED} />
  ) : (
    <SettingsEditor moduleKey="core" base="/platform/settings" canEdit={canEdit} groups={PLATFORM_TAB_GROUPS[tab]} />
  );
}

function TabBody({ tab, canEdit }: { tab: PlatformTabKey; canEdit: boolean }): ReactNode {
  switch (tab) {
    case 'general':
    case 'data':
    case 'advanced':
      return <Groups tab={tab} canEdit={canEdit} />;
    case 'security':
      return (
        <div className="space-y-6">
          <Groups tab={tab} canEdit={canEdit} />
          <Card title={t('platformTabs.entra.title')} description={t('platformTabs.entra.description')}>
            <PlatformEntraSecret canEdit={canEdit} />
          </Card>
        </div>
      );
    case 'email':
      return (
        <div className="space-y-6">
          <Groups tab={tab} canEdit={canEdit} />
          <EmailServerSection canEdit={canEdit} />
        </div>
      );
    case 'sms':
      return <PlatformSmsSection canEdit={canEdit} />;
    case 'wallet':
      return <WalletPlatformCard />;
    case 'sharedReception':
      // The platform-wide switch and defaults of shared reception (one kiosk for several companies): the module's own platform settings.
      return <SettingsEditor moduleKey="shared-reception" base="/platform/settings" canEdit={canEdit} />;
    case 'apps':
      return (
        <div className="space-y-6">
          <Groups tab={tab} canEdit={canEdit} />
          <Card title={t('platformTabs.expo.title')} description={t('platformTabs.expo.description')}>
            <PlatformExpoSecret canEdit={canEdit} />
          </Card>
        </div>
      );
  }
}

/** Platform > Settings: one tab per area, the active tab kept in the address (?tab=). */
export function PlatformSettingsPage() {
  const canEdit = useCan('platform.settings_manage');
  const [tab, setTab] = useTab(PLATFORM_TABS);
  const current = tab as PlatformTabKey;
  return (
    <>
      <PageHeader title={t('nav.platform.settings')} description={t('nav.platform.settings.desc')} />
      <Tabs tabs={PLATFORM_TABS} current={current} onChange={setTab} label={t('platformTabs.label')}>
        <p className="mb-4 text-sm text-slate-600">{t(`platformTabs.${current}.desc`)}</p>
        <TabBody tab={current} canEdit={canEdit} />
      </Tabs>
    </>
  );
}
